import { useEffect, useState } from "react";
import type { SectionArtifact } from "../../client";
import { getSectionCode, getSectionErrorLog, getSectionVideoUrl } from "../../client";

type SectionDetailsProps = {
  section: SectionArtifact;
  directory: string;
};

export function SectionDetails({ section, directory }: SectionDetailsProps) {
  const [code, setCode] = useState<string | null>(null);
  const [errorLog, setErrorLog] = useState<string | null>(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [activeTab, setActiveTab] = useState<"preview" | "code" | "errors">("preview");

  useEffect(() => {
    setCode(null);
    setErrorLog(null);
    setActiveTab("preview");
  }, [section.section_id]);

  const loadCode = async () => {
    if (code !== null) return;
    setLoadingCode(true);
    try {
      const data = await getSectionCode(directory, section.section_id);
      setCode(data);
    } catch (err) {
      setCode("// Failed to load code");
    } finally {
      setLoadingCode(false);
    }
  };

  const loadErrorLog = async () => {
    if (errorLog !== null) return;
    setLoadingError(true);
    try {
      const data = await getSectionErrorLog(directory, section.section_id);
      setErrorLog(data || "No error log available");
    } catch {
      setErrorLog("Failed to load error log");
    } finally {
      setLoadingError(false);
    }
  };

  useEffect(() => {
    if (activeTab === "code") {
      loadCode();
    } else if (activeTab === "errors") {
      loadErrorLog();
    }
  }, [activeTab]);

  const tabs = [
    { id: "preview" as const, label: "Preview" },
    { id: "code" as const, label: "Code" },
    ...(section.has_error_log ? [{ id: "errors" as const, label: "Errors" }] : []),
  ];

  return (
    <div style={{ color: "#e0e0e0" }}>
      {/* Header */}
      <div style={{ marginBottom: "1rem" }}>
        <h3 style={{ margin: "0 0 0.25rem 0", fontSize: "1.125rem", fontWeight: 600 }}>
          {section.title}
        </h3>
        <div style={{ fontSize: "0.75rem", color: "#888" }}>
          Section {section.section_id} • {section.status.replace("_", " ")}
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          borderBottom: "1px solid #333",
          marginBottom: "1rem",
          gap: "0",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "0.75rem 1rem",
              background: "none",
              border: "none",
              borderBottom: activeTab === tab.id ? "2px solid #4a90d9" : "2px solid transparent",
              color: activeTab === tab.id ? "#4a90d9" : "#888",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: activeTab === tab.id ? 600 : 400,
              minHeight: "44px",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "preview" && (
        <PreviewTab section={section} directory={directory} />
      )}
      {activeTab === "code" && (
        <CodeTab code={code} loading={loadingCode} />
      )}
      {activeTab === "errors" && (
        <ErrorsTab errorLog={errorLog} loading={loadingError} />
      )}
    </div>
  );
}

function PreviewTab({
  section,
  directory,
}: {
  section: SectionArtifact;
  directory: string;
}) {
  if (section.status !== "rendered") {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "3rem 1rem",
          color: "#888",
          backgroundColor: "#1a1a1a",
          borderRadius: "8px",
        }}
      >
        <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
          {section.status === "code_only" ? "Code Only" : "Not Rendered"}
        </div>
        <div style={{ fontSize: "0.875rem" }}>
          {section.status === "code_only"
            ? "This section has code but hasn't been rendered yet"
            : "This section hasn't been processed yet"}
        </div>
      </div>
    );
  }

  return (
    <div>
      <video
        src={getSectionVideoUrl(directory, section.section_id)}
        controls
        style={{
          width: "100%",
          maxHeight: "50vh",
          borderRadius: "8px",
          backgroundColor: "#000",
        }}
      />
      {section.video_filename && (
        <div
          style={{
            marginTop: "0.5rem",
            fontSize: "0.75rem",
            color: "#888",
          }}
        >
          {section.video_filename}
        </div>
      )}
    </div>
  );
}

function CodeTab({ code, loading }: { code: string | null; loading: boolean }) {
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
        Loading code...
      </div>
    );
  }

  return (
    <div>
      <pre
        style={{
          backgroundColor: "#0d0d0d",
          padding: "1rem",
          borderRadius: "8px",
          overflow: "auto",
          maxHeight: "60vh",
          fontSize: "0.75rem",
          lineHeight: 1.6,
          margin: 0,
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
        }}
      >
        <code>{code || "// No code available"}</code>
      </pre>
    </div>
  );
}

function ErrorsTab({ errorLog, loading }: { errorLog: string | null; loading: boolean }) {
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
        Loading error log...
      </div>
    );
  }

  return (
    <div>
      <pre
        style={{
          backgroundColor: "#1a0000",
          padding: "1rem",
          borderRadius: "8px",
          overflow: "auto",
          maxHeight: "60vh",
          fontSize: "0.75rem",
          lineHeight: 1.5,
          margin: 0,
          color: "#ef9a9a",
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
        }}
      >
        {errorLog || "No errors"}
      </pre>
    </div>
  );
}
